import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../../utils/logger';
import { 
  Send, 
  Paperclip, 
  X, 
  File, 
  Image, 
  Smile,
  Mic,
  Square,
  Loader
} from 'lucide-react';

const ChatInput = ({ 
  onSendMessage, 
  disabled = false, 
  placeholder = "Type your message...",
  maxLength = 2000
}) => {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const minHeight = 16; // Ultra compact minimum height
      const maxHeight = 60; // Even more reduced maximum height
      textareaRef.current.style.height = Math.min(Math.max(scrollHeight, minHeight), maxHeight) + 'px';
    }
  }, [message]);

  // Handle message submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if ((message.trim() || attachments.length > 0) && !disabled) {
      onSendMessage(message, attachments);
      setMessage('');
      setAttachments([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = '16px';
      }
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    addAttachments(files);
    e.target.value = ''; // Reset input
  };

  // Add attachments with validation
  const addAttachments = (files) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf', 'text/plain', 'text/csv',
      'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      if (!allowedTypes.includes(file.type)) {
        alert(`File type ${file.type} is not supported.`);
        return false;
      }
      return true;
    });

    const newAttachments = validFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));

    setAttachments(prev => [...prev, ...newAttachments]);
  };

  // Remove attachment
  const removeAttachment = (id) => {
    setAttachments(prev => {
      const updated = prev.filter(att => att.id !== id);
      // Clean up preview URLs
      const removed = prev.find(att => att.id === id);
      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      return updated;
    });
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    addAttachments(files);
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const audioFile = new File([audioBlob], `voice-message-${Date.now()}.wav`, { type: 'audio/wav' });
        addAttachments([audioFile]);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      logger.error('Error starting recording:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Common emojis for quick access
  const commonEmojis = ['😊', '👍', '❤️', '😂', '🤔', '👋', '🎉', '💡', '✅', '❌', '📊', '📈'];

  return (
    <div className="relative">
      {/* Drag and Drop Overlay */}
      <AnimatePresence>
        {dragOver && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg flex items-center justify-center z-10"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="text-center">
              <Paperclip className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <p className="text-blue-600 font-medium">Drop files here to attach</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Attachments Preview */}
      <AnimatePresence>
        {attachments.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0, y: -10 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -10 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200"
          >
            <div className="flex items-center space-x-2 mb-3">
              <div className="p-1 bg-blue-100 rounded-lg">
                <Paperclip className="w-4 h-4 text-blue-600" />
              </div>
              <span className="text-sm font-semibold text-blue-900">
                {attachments.length} file{attachments.length > 1 ? 's' : ''} attached
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              {attachments.map((attachment, index) => (
                <motion.div 
                  key={attachment.id} 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ delay: index * 0.1 }}
                  className="relative group"
                >
                  <div className="flex items-center space-x-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all">
                    {attachment.preview ? (
                      <div className="relative">
                        <img 
                          src={attachment.preview} 
                          alt={attachment.name}
                          className="w-10 h-10 object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-blue-500 opacity-0 group-hover:opacity-20 rounded-lg transition-opacity" />
                      </div>
                    ) : (
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <File className="w-6 h-6 text-gray-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {attachment.name}
                      </p>
                      <p className="text-xs text-gray-500 font-medium">
                        {formatFileSize(attachment.size)}
                      </p>
                    </div>
                    <motion.button
                      onClick={() => removeAttachment(attachment.id)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="opacity-0 group-hover:opacity-100 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <X className="w-4 h-4" />
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Emoji Picker */}
      <AnimatePresence>
        {showEmojiPicker && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="absolute bottom-full mb-3 left-0 bg-white border border-gray-200 rounded-2xl shadow-2xl p-4 z-20 backdrop-blur-sm"
          >
            <div className="flex items-center space-x-2 mb-3 pb-2 border-b border-gray-100">
              <Smile className="w-4 h-4 text-yellow-500" />
              <span className="text-sm font-semibold text-gray-700">Quick Emojis</span>
            </div>
            <div className="grid grid-cols-6 gap-2">
              {commonEmojis.map((emoji, index) => (
                <motion.button
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => {
                    setMessage(prev => prev + emoji);
                    setShowEmojiPicker(false);
                  }}
                  className="p-3 hover:bg-yellow-50 rounded-xl text-xl transition-all duration-200 hover:shadow-md"
                >
                  {emoji}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Input Area */}
      <motion.form 
        onSubmit={handleSubmit} 
        className="relative"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div 
          className={`flex items-end space-x-3 p-4 border-2 rounded-2xl transition-all duration-300 shadow-sm hover:shadow-md ${
            dragOver 
              ? 'border-blue-400 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg' 
              : 'border-gray-200 bg-gradient-to-r from-white to-gray-50 hover:border-gray-300'
          } ${disabled ? 'opacity-50' : ''} ${message.trim() ? 'border-blue-300' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Attachment Button */}
          <motion.button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 disabled:opacity-50"
            title="Attach file"
          >
            <Paperclip className="w-5 h-5" />
          </motion.button>

          {/* Text Input */}
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              disabled={disabled}
              maxLength={maxLength}
              className="w-full resize-none border-0 focus:ring-0 focus:outline-none bg-transparent placeholder-gray-400 text-gray-900 font-medium leading-tight"
              rows={1}
              style={{ minHeight: '16px', maxHeight: '60px', lineHeight: '1.2', padding: '4px 0' }}
            />
            
            {/* Character Count */}
            <AnimatePresence>
              {message.length > maxLength * 0.8 && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className={`absolute -bottom-6 right-0 text-xs font-medium px-2 py-1 rounded-full ${
                    message.length >= maxLength 
                      ? 'text-red-600 bg-red-100' 
                      : 'text-amber-600 bg-amber-100'
                  }`}
                >
                  {message.length}/{maxLength}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            {/* Emoji Button */}
            <motion.button
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              disabled={disabled}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className={`p-2 rounded-xl transition-all duration-200 disabled:opacity-50 ${
                showEmojiPicker 
                  ? 'text-yellow-600 bg-yellow-50' 
                  : 'text-gray-500 hover:text-yellow-600 hover:bg-yellow-50'
              }`}
              title="Add emoji"
            >
              <Smile className="w-5 h-5" />
            </motion.button>

            {/* Voice Recording Button */}
            <motion.button
              type="button"
              onClick={isRecording ? stopRecording : startRecording}
              disabled={disabled}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className={`p-2 rounded-xl transition-all duration-200 disabled:opacity-50 ${
                isRecording 
                  ? 'text-red-600 bg-red-50 animate-pulse shadow-lg' 
                  : 'text-gray-500 hover:text-red-600 hover:bg-red-50'
              }`}
              title={isRecording ? 'Stop recording' : 'Start voice recording'}
            >
              {isRecording ? <Square className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </motion.button>

            {/* Send Button */}
            <motion.button
              type="submit"
              disabled={disabled || (!message.trim() && attachments.length === 0)}
              whileHover={{ scale: (message.trim() || attachments.length > 0) && !disabled ? 1.05 : 1 }}
              whileTap={{ scale: (message.trim() || attachments.length > 0) && !disabled ? 0.95 : 1 }}
              className={`p-3 rounded-xl transition-all duration-300 disabled:opacity-50 shadow-sm ${
                (message.trim() || attachments.length > 0) && !disabled
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl'
                  : 'text-gray-400 bg-gray-100 hover:bg-gray-200'
              }`}
              title="Send message"
            >
              {disabled ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <motion.div
                  animate={{
                    rotate: (message.trim() || attachments.length > 0) ? [0, -10, 0] : 0
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <Send className="w-5 h-5" />
                </motion.div>
              )}
            </motion.button>
          </div>
        </div>
      </motion.form>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.txt,.csv,.xlsx,.xls"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

export default ChatInput;